import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { gql, useQuery, useQueryClient } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';

import {
  APPLE_PAY_DPAN_UPDATE_PROMPT,
  ENABLE_BACKUP_CARD_DASHBOARD_BILLING,
  ENABLE_CA_FILING_ACCESS_BANNER,
} from '#client/constants/experimentsFlags';

export const BANNERS_QUERY = gql`
  query Banners(
    $businessEntityUuid: UUID!
    $enableBackupCardBanner: Boolean
    $enableCAFilingAccessBanner: Boolean
    $enableUpdatePaymentMethodBanner: Boolean
  ) {
    banners(
      filter: {
        businessEntityUuid: $businessEntityUuid
        experiment: {
          enableBackupCardBanner: $enableBackupCardBanner
          enableCAFilingAccessBanner: $enableCAFilingAccessBanner
          enableUpdatePaymentMethodBanner: $enableUpdatePaymentMethodBanner
        }
      }
    ) {
      title
      description
      type
      imageUrl
      imageSize
      raised
      dismissible
      storageKey
      experiment {
        featureFlagKey
        featureFlagVariation
      }
      primaryCta {
        label
        modalName
        url
      }
      secondaryCta {
        label
        modalName
        url
      }
      clickstream {
        elementName
        ctaElementName
        dynamicProperties
      }
    }
  }
`;

export function useBanners() {
  const { businessEntityUuid } = useBusinessEntity();
  const enableBackupCardBanner = useExperimentFlag(ENABLE_BACKUP_CARD_DASHBOARD_BILLING, false);
  const enableCAFilingAccessBanner = useExperimentFlag(ENABLE_CA_FILING_ACCESS_BANNER, false);
  const enableUpdatePaymentMethodBanner = useExperimentFlag(APPLE_PAY_DPAN_UPDATE_PROMPT, false);

  const client = useQueryClient();
  const variables = {
    businessEntityUuid,
    enableBackupCardBanner,
    enableCAFilingAccessBanner,
    enableUpdatePaymentMethodBanner,
  };

  // Stale-while-revalidate (ECOMM-2812): banners reflect account state other pages change (e.g.
  // updating Apple Pay on the billing MFE), so every mount refetches; until it lands, the last
  // result stays on screen instead of the banner popping in.
  const { data, loading, error } = useQuery(BANNERS_QUERY, {
    noThrow: true,
    skip: !businessEntityUuid,
    skipCache: true,
    variables,
  });
  const cachedBanners = loading
    ? client.cache.readQuery({ query: BANNERS_QUERY, variables, operationName: 'Banners' })?.data?.banners
    : undefined;

  useError(error);

  return {
    data: (loading ? cachedBanners : data?.banners) || [],
    error,
    loading: loading && !cachedBanners,
  };
}
